import type { SalesQueryContext } from '@/utils/sales-query-context'
import type { FamilyPublishedPriceItem } from '../rate-share/family-types'
import { request as http } from '@/utils/http'
import type { PageRequest, PageResponse } from '@/types/api'
import type {
  PriceExpressFirstAdditionalDTO,
  PriceExpressUnitPriceDTO,
  PriceExpressWeightTierDTO,
  PriceRegionDTO
} from '@/api/sass/rate-management/types'
import type { BusinessIncreaseRuleExpressPrice } from '../business-increase-rules/types'

export interface PriceQueryExpressContext {
  queryDate?: string
  loadMode?: string
  pieces?: number
  actualWeight?: number | string
  lengthCm?: number | string
  widthCm?: number | string
  heightCm?: number | string
  totalVolume?: number | string
  totalWeightTon?: number | string
  expectedVehicleType?: string
  pricingPreference?: string
  pickupRequired?: boolean
  deliveryRequired?: boolean
  loadingRequired?: boolean
}

export const PRICE_QUERY_EXPRESS_SORT_FIELD = {
  PRICE_NO: 'priceNo',
  SERVICE_TYPE: 'serviceType',
  LOAD_MODE: 'loadMode',
  ORIGIN_TEXT: 'originText',
  DESTINATION_TEXT: 'destinationText',
  BILLING_MODEL: 'billingModel',
  SURCHARGE_COUNT: 'surchargeCount',
  TOTAL_AMOUNT: 'totalAmount',
  BEGIN_DATE: 'beginDate'
} as const

export type PriceQueryExpressSortField = typeof PRICE_QUERY_EXPRESS_SORT_FIELD[keyof typeof PRICE_QUERY_EXPRESS_SORT_FIELD]
export type PriceQueryExpressSortDirection = 'asc' | 'desc'

export interface PriceQueryExpressSortCondition {
  fieldName: PriceQueryExpressSortField
  direction: PriceQueryExpressSortDirection
}

export interface PriceQueryExpressPageRequest extends Omit<PageRequest, 'sorts'>, SalesQueryContext {
  queryContext?: PriceQueryExpressContext
  sorts?: PriceQueryExpressSortCondition[]
}

export interface PriceQueryExpressGetRequest extends SalesQueryContext {
  priceId: string
  queryContext?: PriceQueryExpressContext
}

export interface PriceQueryExpressFeeItemVO {
  chargeItemId?: string
  chargeCode?: string
  chargeName?: string
  feeSource?: number
  surchargeRuleId?: string
  billingMode?: string
  currency?: string
  measure?: string
  measureNameCn?: string
  measureNameEn?: string
  paymentTerm?: string
  unitRate?: number | string | null
  firstWeightThreshold?: number | string | null
  firstWeightPrice?: number | string | null
  additionalWeightUnit?: number | string | null
  additionalWeightPrice?: number | string | null
  quantity?: number | string | null
  minCharge?: number | string | null
  amount?: number | string | null
  countTotal?: boolean
  totalIncluded?: boolean
  remarks?: string
}

export interface PriceQueryExpressCalculationVO {
  volumeWeight?: number | string | null
  billingWeight?: number | string | null
  baseFreight?: number | string | null
  minCharge?: number | string | null
  chargeableBase?: number | string | null
  surchargeTotal?: number | string | null
  totalAmount?: number | string | null
  recommended?: boolean
  calculationFormula?: string
  calculationWarnings?: string[]
  calculationComplete?: boolean
  currencyTotals?: PriceQueryExpressCurrencyTotalVO[]
  chargingItems?: PriceQueryExpressFeeItemVO[]
}

export interface PriceQueryExpressCurrencyTotalVO {
  currency?: string
  baseFreight?: number | string | null
  surchargeTotal?: number | string | null
  percentageRate?: number | string | null
  totalAmount?: number | string | null
  complete?: boolean
}

export interface PriceQueryExpressListVO {
  /** 接收方伙伴冻结最终价来源，已有salesCalculation可直接作为展示试算。 */
  rateSource?: 'PARTNER_SHARE'
  id: string
  priceNo: string
  source?: number
  officeId?: string
  loadMode?: string
  supplierId?: string
  accountNo?: string
  serviceType?: string
  cargoType?: string
  routeType?: string
  billingModel?: string
  billingMethod?: string
  currency?: string
  beginDate?: string
  endDate?: string
  status?: number
  specialPriceStatus?: number | string | null
  volumetricFactor?: number | string | null
  minBillingWeight?: number | string | null
  pickupFee?: number | string | null
  deliveryFee?: number | string | null
  loadingFee?: number | string | null
  vehicleType?: string
  vehicleCapacity?: number | string | null
  vehicleVolume?: number | string | null
  pricingMethod?: string
  /** 接口原始整车按趟成本价；销售视图读取 businessPrice。 */
  tripPrice?: number | string | null
  /** 接口原始每公里成本单价；销售视图读取 businessPrice。 */
  kmPrice?: number | string | null
  minKm?: number | string | null
  onePrice?: boolean | null
  /** 接口原始一口价成本总额；销售视图读取 businessPrice。 */
  onePriceAmount?: number | string | null
  onePriceCurrency?: string
  onePriceIncludedItems?: string
  onePriceRemarks?: string
  /** 接口原始首续重成本明细；销售视图仅替换单价，保留重量和最低消费。 */
  firstAdditional?: PriceExpressFirstAdditionalDTO
  /** 接口原始单价成本明细；销售视图保留计费单位和最低消费。 */
  unitPrice?: PriceExpressUnitPriceDTO
  /** 接口原始重量段成本明细；销售视图保留段边界和最低消费。 */
  weightTiers?: PriceExpressWeightTierDTO[]
  surchargeCount?: number
  originText?: string
  destinationText?: string
  createdBy?: string
  dateCreated?: string
  modifiedBy?: string
  dateModified?: string
  /** 接口成本计算结果，供应付费用预览；销售视图使用 salesCalculation。 */
  calculation?: PriceQueryExpressCalculationVO
  /** 服务端基于商务销售明细计算的费用；前端不得再次叠加商务加价。 */
  salesCalculation?: PriceQueryExpressCalculationVO
  /** 命中规则及各金额的原始成本、加价和销售价，保留成本以供追溯。 */
  businessPrice?: BusinessIncreaseRuleExpressPrice
  salesPrice?: BusinessIncreaseRuleExpressPrice
  /** Family手动分享冻结的完整最终价表；普通维护查询可不返回。 */
  publishedPriceItems?: FamilyPublishedPriceItem[] | null
}

export interface PriceQueryExpressDetailVO extends PriceQueryExpressListVO {
  sourceId?: string
  relatedNo?: string
  externalId?: string
  externalSource?: string
  paymentTerm?: string
  remarks?: string
  billingRemark?: string
  batchNo?: string
  regionItems?: PriceRegionDTO[]
}

export const page = (params: PriceQueryExpressPageRequest) => {
  return http.post<PageResponse<PriceQueryExpressListVO>>('/price/query/express/page', params)
}

export const get = (params: PriceQueryExpressGetRequest) => {
  return http.post<PriceQueryExpressDetailVO>('/price/query/express/get', params)
}

export const feePreview = (params: PriceQueryExpressGetRequest) => {
  return http.post<PriceQueryExpressCalculationVO>('/price/query/express/fee-preview', params)
}
